import { Component, computed, inject, input, Input, OnInit } from '@angular/core';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { ShopService } from '@app/shared/services/shop.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import {
  ShopOffersCreatePurchaseResponse,
  ShopOffersItem,
  ShopOffersItemType,
  ShopOffersProvider,
  Tag,
} from '@shared/models/shop-offers.model';
import { User } from '@shared/models/user.model';
import { takeUntil } from 'rxjs/operators';
import { NativeService } from '@shared/services/native.service';
import { TokenService } from '@shared/services/token.service';
import { Observable } from 'rxjs';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { FormatShopPrice } from '../../pipes/format-shop-price.pipe';
import { SharedModule } from '@app/shared/shared.module';
import { LimitedTimeEventType } from '@app/shared/models/limitedTimeEvent.model';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { toSignal } from '@angular/core/rxjs-interop';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-shop-item',
  templateUrl: './shop-item.component.html',
  styleUrls: ['./shop-item.component.scss'],
  standalone: true,
  imports: [DisplayComponent, ButtonComponent, SharedModule, FormatShopPrice],
})
export class ShopItemComponent implements OnInit {
  type = input<ItemType>();
  @Input() user: User;
  @Input() item: ShopOffersItem;
  @Input() active: boolean;
  @Input() index: number;

  private readonly shopService = inject(ShopService);
  private readonly eventManager = inject(EventManager);
  public readonly logger = inject(LoggerService);
  public readonly nativeService = inject(NativeService);
  public readonly tokenService = inject(TokenService);
  private readonly eventsRepo = inject(EventsRepository);

  totalPrice: string;
  tag: string;

  componentDestroy: () => Observable<unknown>;

  private readonly shopOfferItemType = computed<ShopOffersItemType>(() => {
    switch (this.type()) {
      case 'coin':
        return 'Coins';
      case 'gem':
        return 'Gems';
      default:
        return 'Coins';
    }
  });

  private readonly visibleEvents = toSignal(this.eventsRepo.visibleLimitedTimeEvents$, { initialValue: [] });

  multiplierPrize = computed(
    () =>
      this.visibleEvents().find(
        (e) =>
          e.type === LimitedTimeEventType.MultiplierPrize && e.properties?.shopcurrency === this.shopOfferItemType()
      ) ?? null
  );

  magicbag = computed(
    () =>
      this.visibleEvents().find(
        (e) =>
          e.type === LimitedTimeEventType.Magicbag &&
          Number(this.item.amount) >= Number(e.properties?.minlocalprice) &&
          e.properties?.shopcurrency === this.shopOfferItemType()
      ) ?? null
  );

  shopOfferProvider: ShopOffersProvider;

  ngOnInit() {
    this.parseShopOffersGenericTagValue();
    this.shopOfferProvider = this.getShopOffersProvider();
    this.setTag();
  }

  public createPurchase() {
    if (this.active) {
      this.shopService
        .createPurchaseShopOffers(
          this.user,
          this.item,
          this.shopOfferItemType(),
          this.shopOfferProvider,
          this.tokenService.getCurrentInfo().shopLevel
        )
        .pipe(takeUntil(this.componentDestroy()))
        .subscribe({
          next: (purchaseResponse: ShopOffersCreatePurchaseResponse) => {
            this.shopService.notifyCreatePurchaseShopOffers(
              this.shopOfferProvider,
              purchaseResponse,
              this.item.isoCurrency
            );
          },
          error: (e) => {
            this.logger.error(e);
            this.handleCreatePurchaseError(e.status);
          },
        });
    }
  }

  /**
   * TODO: Extract this functions to a utils common service.
   */
  private getShopOffersProvider(): ShopOffersProvider {
    switch (this.nativeService.nativeInfo.deviceType) {
      case 'android':
        return ShopOffersProvider.googlePlay;
      case 'ios':
        return ShopOffersProvider.appleAppStore;
      default:
        return ShopOffersProvider.adyen;
    }
  }

  private handleCreatePurchaseError(errorCode: number) {
    switch (errorCode) {
      case 400:
        this.eventManager.openModal(
          new Modal(NonPersistedModalName.PurchaseErrorModalComponent, ModalType.View, { errorCode }, 'error-dialog')
        );
        break;
      case 422:
        this.eventManager.openModal(
          new Modal(NonPersistedModalName.PurchaseErrorModalComponent, ModalType.View, { errorCode }, 'error-dialog')
        );
        break;
      default:
        break;
    }
  }

  /*
   *  Due to the other products needs and specifications,
   *  tags types comes in a diferent standard.
   */
  public parseShopOffersGenericTagValue() {
    switch (this.item.tag) {
      case 'Normal':
        this.item.tag = Tag.none;
        break;
      case 'Most Popular':
        this.item.tag = Tag.mostPopular;
        break;
      case 'Best Value':
        this.item.tag = Tag.bestValue;
        break;
      default:
        break;
    }
  }

  private setTag() {
    const tagMap = {
      [Tag.bestValue]: 'best-value-tag',
      [Tag.mostPopular]: 'most-popular-tag',
    };

    const baseTag = tagMap[this.item.tag] || 'default-tag';
    this.tag = baseTag;
  }
}

export type ItemType = 'coin' | 'gem';
